<template>
  <div class="voi-card">
    <div class="row">
      <div class="col-sm-4 order-2 order-sm-1 pr-0 voi-details-container">
        <div class="p-4">
          <div class="row mx-0">
            <div class="col pr-0 p-sm-0 order-2 order-sm-1">
              <div class="vehicle-year">
                {{ vehicle.year
                }}<template v-if="isUsedVehicle"
                  ><span class="px-2">•</span>{{ $n(vehicle.kilometers, 'number', languageCode) }}
                  {{ $t('message.credit.inventoryMatching.km') }}</template
                >
              </div>
              <div class="vehicle-name pb-1">
                {{ vehicleName }}
              </div>
            </div>
            <div class="order-1 order-sm-2 image-container">
              <img
                v-image-fallback="require('@/assets/vehicle-fallback.png')"
                :src="vehicle.photoURL"
                :alt="vehicleName"
              />
            </div>
          </div>
          <MobialisButton
            :class="{ 'd-sm-none': !voiMatchSuccess }"
            class="mb-1 mt-2"
            type="button"
            size="btn-sm"
            variant="primary"
            @click="goToFinanceFlow"
            v-av-analytics-cta-interaction="{
              elementText: $t('message.credit.applyNowTitle'),
              eventActionResult: 'open',
            }"
            >{{ $t('message.credit.applyNowTitle') }}</MobialisButton
          >
        </div>
      </div>
      <div class="col-sm-8 order-1 order-sm-2 payment-details-container">
        <div v-if="voiMatchSuccess">
          <div class="p-4 pt-0 pt-sm-5 payment-details">
            <div class="vehicle-cost-heading pb-2">
              {{ $t('message.credit.inventoryMatching.vehicleCost', { message: getPriceTaxDisclaimerMessage }) }}
            </div>
            <div class="vehicle-price pb-3 mb-sm-3">{{ $n(vehicle.price, 'currencyDollars', languageCode) }}</div>
            <div class="row vehicle-payment-and-term-container">
              <div class="col-4">
                <div class="vehicle-payment-and-term mb-1">
                  {{ $t('message.credit.inventoryMatching.payment') }}
                </div>
                <div class="vehicle-payment-and-term-amounts mt-0">
                  {{ $n(vehicleData.paymentMonthly, 'currencyDollars', languageCode)
                  }}{{ $t('message.credit.inventoryMatching.perMonth') }}
                </div>
              </div>
              <div class="col">
                <div class="vehicle-payment-and-term mb-1">
                  {{ $t('message.credit.inventoryMatching.term') }}
                </div>
                <div class="vehicle-payment-and-term-amounts mt-0">
                  {{ vehicleData.term }} {{ $t('message.credit.inventoryMatching.months') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="p-4">
          <div class="no-match-title pb-2 mb-sm-1">
            {{ $t('message.credit.inventoryMatching.noVOIMatchTitle') }}
          </div>
          <div class="no-match-description p-0 pb-sm-4">
            {{ $t('message.credit.inventoryMatching.noVOIMatchDescription') }}
          </div>
          <MobialisButton
            class="d-none d-sm-block"
            type="button"
            size="btn-sm"
            variant="primary"
            @click="goToFinanceFlow"
            v-av-analytics-cta-interaction="{
              elementText: $t('message.credit.applyNowTitle'),
              eventActionResult: 'open',
            }"
            >{{ $t('message.credit.applyNowTitle') }}</MobialisButton
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperFunctions from '@/utils/helperFunctions';
import ImageFallback from '@/utils/directives/imageFallback';

export default {
  name: 'VehicleOfInterestCard',
  props: {
    vehicleData: {
      type: Object,
      required: true,
    },
  },
  directives: {
    imageFallback: ImageFallback,
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      integrationProvince: 'business/getIntegrationProvince',
    }),
    voiMatchSuccess() {
      return this.vehicleData.isMatched;
    },
    vehicle() {
      return this.vehicleData.vehicle;
    },
    vehicleName() {
      return helperFunctions.getVehicleName(this.vehicle, ['make', 'model', 'trim']);
    },
    isUsedVehicle() {
      return this.vehicle.status === 'USED';
    },
    getPriceTaxDisclaimerMessage() {
      const provinceDisclaimer = helperFunctions.getDisclaimerByProvince(this.integrationProvince);
      return this.$t(provinceDisclaimer, { prefix: '(', postfix: ')' });
    },
  },
  methods: {
    goToFinanceFlow() {
      const payload = {
        elementInteractedWith: 'cim.applyForFinancingStart',
        vehicleId: this.vehicleData.id,
        isInitialVoiEvent: true,
      };
      this.goToAVFinanceFlow(this.vehicleData);
      this.$store.dispatch('metrics/logModalInteraction', payload).catch(error => {
        this.$log('Error in logging interaction', error);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';
.voi-card {
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  .voi-details-container {
    background: #fff;
    .vehicle-year {
      color: #4b5863;
      font-size: 10px;
      line-height: 18px;
    }
    .vehicle-name {
      color: #19222b;
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
    }
    .image-container {
      width: 100%;
      position: relative;
      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: cover;
      }
      /* Mobile only*/
      @media (max-width: $breakpoint-mobile) {
        width: 138px;
      }
    }
  }

  .payment-details-container {
    background-color: $primary-light-background;
    .vehicle-cost-heading {
      color: #4b5863;
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      /* Mobile only*/
      @media (max-width: $breakpoint-mobile) {
        font-size: 14px;
        font-weight: 700;
        line-height: 17.5px;
      }
    }
    .vehicle-price {
      color: #19222b;
      font-size: 28px;
      font-weight: 700;
      line-height: 35px;
      /* Mobile only*/
      @media (max-width: $breakpoint-mobile) {
        font-size: 20px;
        line-height: 25px;
      }
    }
    .vehicle-payment-and-term {
      color: #4b5863;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
    }
    .vehicle-payment-and-term-amounts {
      color: #000000;
      font-size: 14px;
      font-weight: 700;
      line-height: 17.5px;
    }
    .no-match-title {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
    }
    .no-match-description {
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
    }
  }
}
</style>
